import { Box, Button, Dialog, DialogContent, DialogTitle, FormGroup, FormHelperText, TextField, Typography } from "@mui/material";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { ApiResponseType } from "../../types";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SectionAppBar from "../../v2/components/Section/SectionAppBar";
import SimpleDialog from "../common/SimpleDialog";
import SaveButton from "../../v2/components/Button/SaveButton";
import StandardButton from "../../v2/components/Button/StandardButton";
import SaveAltIcon from '@mui/icons-material/SaveAlt';

type Props<T> = {
    open: boolean,
    onClose: () => void,
    onSubmit: (data: T) => Promise<ApiResponseType>,
    title: string,
    subTitle: string,
    fieldLabel: string,
    confirmButtonLabel: string,
    record: T
}

const NameDialog = <T extends { name: string }>({ open, onClose, onSubmit, title, subTitle, fieldLabel, confirmButtonLabel, record }: Props<T>) => {
    const {
        control,
        setValue,
        handleSubmit,
        setError,
        formState: { errors }
    } = useForm({ defaultValues: { name: '' } })

    useEffect(() => {
        setValue('name', record.name);
    }, [record])

    const onSaveRecord = async (data: T) => {
        const response = await onSubmit({ ...record, ...data });

        if (response.succeeded) {
            setValue('name', '');
            onClose();
        } else {
            setError('name', { message: response.errors[0].description });
        }
    }

    return (
        <SimpleDialog
            open={open}
            onClose={onClose}
            padding={0}
            fullHeight={false}
            size='sm'
        >
            <SectionAppBar
                title={title ?? `Rename Playbook`}
                subTitle={''}
                buttons={
                    <>
                        <SaveButton size='small' type='submit' variant='contained' onClick={handleSubmit(onSubmit)} icon={<SaveAltIcon />}>
                            {confirmButtonLabel ?? 'Save'}
                        </SaveButton>
                        <StandardButton size='small' variant='outlined' color='secondary' onClick={onClose} icon={<CloseIcon />}>
                            Cancel
                        </StandardButton>
                    </>
                }
            />
            <DialogContent>
                <Box component='form' padding={3} onSubmit={handleSubmit(onSaveRecord)}>
                    <FormGroup sx={{ mb: 1 }}>
                        <Controller
                            name='name'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={value}
                                    label={fieldLabel}
                                    onChange={onChange}
                                    error={Boolean(errors.name)}
                                    placeholder={`Enter ${fieldLabel}`}
                                />
                            )}
                        />
                        {errors.name && (
                            <FormHelperText sx={{ color: 'error.main' }}>{errors.name.message ? errors.name.message : <>Please enter a valid {fieldLabel.toLowerCase()}</>}</FormHelperText>
                        )}
                    </FormGroup>
                </Box>
            </DialogContent>
        </SimpleDialog>

    )
}

export { NameDialog }
export default NameDialog;